import { getDB } from "./schema.js";
import { toCloudRow, upsertCloudRows } from "./cloud-items.js";
import { toCloudGroup, upsertCloudGroups } from "./cloud-groups.js";
import { toCloudHistory, upsertCloudHistory, resetHistoryWatermark } from "./cloud-history.js";
import { resetFollowsSynced } from "./cloud-follows.js";

// One-time first-signup migration of a device's existing local-only collection
// into the user's cloud account, plus the local-data wipe used by discard and
// sign-out. Works against the IndexedDB stores directly (not games.js/books.js)
// so it neither pulls the sync engine into an import cycle nor re-enqueues cloud
// ops — the bulk push and the _synced stamping are done here explicitly.

// Per-device flag: once a device has migrated or discarded, don't prompt again.
const FLAG = "mt_migration_completed";

export function isMigrationDone() {
	return localStorage.getItem(FLAG) === "true";
}
export function markMigrationDone() {
	localStorage.setItem(FLAG, "true");
}
export function clearMigrationFlag() {
	localStorage.removeItem(FLAG);
}

// Every synced item store: its key in a counts object, its media type, and how
// one / many read. The first-sign-in migration covers all of them (it used to
// skip TV and anime).
const ITEM_STORES = [
	{ store: "games", key: "games", media: "videogame", one: "game", many: "games" },
	{ store: "books", key: "books", media: "book", one: "book", many: "books" },
	{ store: "tv", key: "tv", media: "tv", one: "TV series", many: "TV series" },
	{ store: "anime", key: "anime", media: "anime", one: "anime", many: "anime" },
	{ store: "movies", key: "movies", media: "movie", one: "movie", many: "movies" },
	{ store: "comics", key: "comics", media: "comic", one: "comic", many: "comics" },
];

export async function localDataCounts() {
	const db = await getDB();
	const counts = {};
	for (const s of ITEM_STORES) counts[s.key] = await db.count(s.store);
	counts.groups = await db.count("groups");
	counts.total = Object.values(counts).reduce((a, b) => a + b, 0);
	return counts;
}

// Human phrasing of a counts object, e.g. "3 games, 2 books and 1 group" (drops
// zero sides). Shared by the welcome note and the migration prompt.
export function describeCounts(counts) {
	const parts = [];
	for (const s of ITEM_STORES) {
		const n = counts?.[s.key] ?? 0;
		if (n > 0) parts.push(`${n} ${n === 1 ? s.one : s.many}`);
	}
	const groups = counts?.groups ?? 0;
	if (groups > 0) parts.push(`${groups} group${groups === 1 ? "" : "s"}`);
	if (parts.length <= 1) return parts.join("");
	return `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
}

// Push every local item (all six categories) in one batched upsert, then mark
// each local record cloud-backed. On failure the local data is left untouched
// (nothing is stamped, flag not set) so the user can retry. Returns the count.
export async function migrateLocalToCloud(userId) {
	const db = await getDB();
	const byStore = {};
	for (const s of ITEM_STORES) byStore[s.store] = await db.getAll(s.store);
	const groups = await db.getAll("groups");

	const rows = ITEM_STORES.flatMap((s) => byStore[s.store].map((r) => toCloudRow(r, s.media, userId)));

	await upsertCloudRows(rows);
	await upsertCloudGroups(groups.map((g) => toCloudGroup(g, userId)));
	// History: one batch per 500 events, then stamped below.
	const history = await db.getAll("history");
	for (let i = 0; i < history.length; i += 500) {
		await upsertCloudHistory(history.slice(i, i + 500).map((e) => toCloudHistory(e, userId)));
	}

	// Mark cloud-backed so the sync reconciler treats them as synced (direct
	// put — no updated_at bump, no enqueue).
	for (const s of ITEM_STORES) {
		for (const r of byStore[s.store]) await db.put(s.store, { ...r, _synced: true });
	}
	for (const e of history) {
		await db.put("history", { ...e, _synced: true });
	}
	for (const grp of groups) {
		grp._synced = true;
		await db.put("groups", grp);
	}

	markMigrationDone();
	return rows.length;
}

// Clear the synced stores. Groups are now cloud-backed, so they're cleared too
// (re-pulled on the next sign-in). Rankings stay device-local and unsynced, so
// they're deliberately preserved. Shared by discard and sign-out.
export async function wipeLocalCollection() {
	const db = await getDB();
	await db.clear("games");
	await db.clear("books");
	await db.clear("tv");
	await db.clear("anime");
	await db.clear("movies");
	await db.clear("comics");
	await db.clear("groups");
	await db.clear("pending_operations");
	// History is cloud-backed (re-pulled on the next sign-in from scratch).
	await db.clear("history");
	resetHistoryWatermark();
	// Follows stay on the device (they were device-local before sync); the next
	// sign-in merges them with that account's from scratch.
	resetFollowsSynced();
	// Notifications are a device-local ARCHIVE now (design_documents/history.md
	// decision 6) — kept; only the release scan restarts.
	localStorage.removeItem("notif_release_last_checked");
	// The daily catalog refresh is per-library too: let the next sign-in start fresh.
	localStorage.removeItem("catalog_refresh_last_run");
}

export async function discardLocalData() {
	await wipeLocalCollection();
	markMigrationDone();
}
